/**
 * ApplicationLockTakeoverDialog
 * Modal displayed when user clicks a locked application, offering option to take over.
 */

import * as React from "react";
import {
  AlertCircleIcon,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@porchpass/ui";
import { formatUserDisplay } from "../utils/userUtils";

interface LockedByUser {
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface ApplicationLockTakeoverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTakeover: () => Promise<void>;
  lockedByUser: LockedByUser | null;
  isTakingOver?: boolean;
}

export const ApplicationLockTakeoverDialog: React.FC<
  ApplicationLockTakeoverDialogProps
> = ({
  open,
  onOpenChange,
  onTakeover,
  lockedByUser,
  isTakingOver = false,
}) => {
  const handleTakeover = () => {
    void (async () => {
      try {
        await onTakeover();
        onOpenChange(false);
      } catch (error) {
        console.error(
          "[ApplicationLockTakeoverDialog] Takeover failed:",
          error,
        );
      }
    })();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertCircleIcon size={20} className="text-[#df1b41]" />
            <DialogTitle>Application Locked</DialogTitle>
          </div>
        </DialogHeader>
        <DialogBody>
          <p className="text-base text-[#4a5466]">
            This application is currently being edited by{" "}
            <strong>{formatUserDisplay(lockedByUser)}</strong>.
          </p>
          <p className="text-base text-[#4a5466] mt-3">
            Do you want to take over the application? The current user will be
            notified and will no longer be able to edit the application.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button
            onClick={() => onOpenChange(false)}
            variant="secondary"
            size="s"
            disabled={isTakingOver}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTakeover}
            disabled={isTakingOver}
            loading={isTakingOver}
            size="s"
          >
            {isTakingOver ? "Taking Over..." : "Take Over"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
